import { Box } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import slide1 from "../../assets/slide1.png";
import slide2 from "../../assets/slide2.png";
import slide3 from "../../assets/slide3.png";
import slide4 from "../../assets/slide4.png";
import slide5 from "../../assets/slide5.png";
import slide6 from "../../assets/slide6.png";
import { useClient } from "../../app/context/ClientContext";
import { getClientBackgroundSlides } from "../../core/services/client.service";
import ThemeBackdrop from "./ThemeBackdrop";

const DEFAULT_SLIDES = [slide1, slide2, slide3, slide4, slide5, slide6].map(
  (url) => ({
    url,
    fileName: "",
    backgroundFit: "fill",
    overlay: 0,
  }),
);

const getClientId = (client) =>
  client?.clientId ?? client?.Id ?? client?.id ?? null;

const normalizeSlide = (slide) => {
  if (!slide) return null;
  if (typeof slide === "string") {
    return {
      url: slide,
      fileName: "",
      backgroundFit: "fill",
      overlay: 0,
    };
  }
  if (!slide.url) return null;
  return {
    url: slide.url,
    fileName: slide.fileName || "",
    backgroundFit: slide.backgroundFit || "fill",
    overlay: Number(slide.overlay) || 0,
  };
};

const areSlidesEqual = (left, right) =>
  left.length === right.length &&
  left.every(
    (slide, index) =>
      slide.url === right[index].url &&
      slide.backgroundFit === right[index].backgroundFit &&
      slide.overlay === right[index].overlay,
  );

export default function Background({ forceDefault = false } = {}) {
  const { selectedClient } = useClient();
  const clientId = forceDefault ? null : getClientId(selectedClient);
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesRef = useRef(DEFAULT_SLIDES);

  const updateSlides = useCallback((nextSlides) => {
    const normalized = (nextSlides ?? []).map(normalizeSlide).filter(Boolean);
    if (areSlidesEqual(slidesRef.current, normalized)) return;

    slidesRef.current = normalized;
    setSlides(normalized);
    setCurrentSlide(0);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSlides = async () => {
      if (!clientId) {
        if (!cancelled) updateSlides(DEFAULT_SLIDES);
        return;
      }

      try {
        const clientSlides = await getClientBackgroundSlides(clientId);
        if (cancelled) return;
        updateSlides(clientSlides?.length ? clientSlides : DEFAULT_SLIDES);
      } catch {
        if (!cancelled) updateSlides(DEFAULT_SLIDES);
      }
    };

    loadSlides();

    const onThemeUpdated = () => {
      loadSlides();
    };
    window.addEventListener("client-theme-updated", onThemeUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener("client-theme-updated", onThemeUpdated);
    };
  }, [clientId, updateSlides]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 60000);

    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        overflow: "hidden",
      }}
    >
      {slides.map((slide, index) => {
        const isActive = index === currentSlide;

        return (
          <Box
            key={`${index}-${slide.url}`}
            sx={{
              position: "absolute",
              inset: 0,
              opacity: isActive ? 1 : 0,
              zIndex: isActive ? 2 : 1,
              pointerEvents: "none",
              transition: "opacity 2s ease-in-out",
            }}
          >
            <ThemeBackdrop
              url={slide.url}
              fit={slide.backgroundFit}
              overlay={slide.overlay}
              blur={8}
            />
          </Box>
        );
      })}
    </Box>
  );
}
